import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { Info, Loader2, Sparkles } from "lucide-react";
import { estimateJobPrice, type PriceEstimate } from "@/lib/pricing.functions";

type Props = {
  categorySlug: string;
  postcode: string;
  estimatedDurationMinutes?: number;
  urgency?: "low" | "normal" | "high";
  complexity?: "low" | "normal" | "high";
  compact?: boolean;
  className?: string;
};

const ALLOWED = new Set([
  "handyman", "tv-mounting", "furniture-assembly", "moving",
  "furniture-removal", "heavy-lifting", "ikea-assembly", "furniture-disassembly",
  "shelf-mounting", "picture-hanging", "curtain-blind-installation",
  "home-repairs", "appliance-installation", "door-repair", "drywall-repair",
]);

function fmtGBP(n: number): string {
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
    maximumFractionDigits: 0,
  }).format(n);
}

export function PriceEstimateCard({
  categorySlug,
  postcode,
  estimatedDurationMinutes,
  urgency,
  complexity,
  compact = false,
  className,
}: Props) {
  const estimate = useServerFn(estimateJobPrice);
  const [data, setData] = useState<PriceEstimate | null>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const valid =
    ALLOWED.has(categorySlug) && postcode.replace(/\s+/g, "").length >= 5;

  useEffect(() => {
    if (!valid) {
      setData(null);
      setErr(null);
      return;
    }
    const ctrl = new AbortController();
    setLoading(true);
    setErr(null);
    const t = window.setTimeout(async () => {
      try {
        const result = await estimate({
          data: {
            category_slug: categorySlug as "handyman",
            postcode,
            estimated_duration_minutes: estimatedDurationMinutes,
            urgency,
            complexity,
          },
        });
        if (!ctrl.signal.aborted) setData(result);
      } catch (e) {
        if (!ctrl.signal.aborted) {
          setErr(e instanceof Error ? e.message : "Couldn't estimate right now");
          setData(null);
        }
      } finally {
        if (!ctrl.signal.aborted) setLoading(false);
      }
    }, 350);
    return () => {
      ctrl.abort();
      window.clearTimeout(t);
    };
  }, [valid, categorySlug, postcode, estimatedDurationMinutes, urgency, complexity, estimate]);

  if (!valid) return null;

  return (
    <section
      aria-labelledby="price-estimate-heading"
      className={`overflow-hidden rounded-2xl border border-border bg-card shadow-sm ${className ?? ""}`}
    >
      <div className="flex items-center justify-between gap-2 border-b border-border bg-muted/30 px-4 py-2.5">
        <h3
          id="price-estimate-heading"
          className="flex items-center gap-1.5 text-sm font-semibold"
        >
          <Sparkles className="h-3.5 w-3.5 text-primary" aria-hidden />
          Estimated price range
        </h3>
        {data && (
          <span
            className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${
              data.confidence_label === "high"
                ? "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400"
                : data.confidence_label === "medium"
                ? "bg-amber-500/10 text-amber-700 dark:text-amber-400"
                : "bg-muted text-muted-foreground"
            }`}
            title={`Confidence ${Math.round(data.confidence * 100)}% (${data.basis.data_source.replace(/_/g, " ")}, n=${data.basis.sample_size})`}
          >
            {data.confidence_label[0].toUpperCase() + data.confidence_label.slice(1)} confidence
          </span>
        )}
      </div>

      <div className="p-4">
        {loading && !data && (
          <p className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> Calculating estimate for your area…
          </p>
        )}

        {err && !loading && (
          <p className="text-xs text-muted-foreground">{err}</p>
        )}

        {data && (
          <>
            <div className={`grid gap-2 ${compact ? "grid-cols-3" : "grid-cols-3"}`}>
              <Tier label="Low" value={fmtGBP(data.low)} tone="muted" />
              <Tier label="Expected" value={fmtGBP(data.expected)} tone="primary" />
              <Tier label="Premium" value={fmtGBP(data.premium)} tone="muted" />
            </div>

            <p className="mt-3 text-[11px] leading-relaxed text-muted-foreground">
              Based on {data.basis.data_source === "historical_bookings"
                ? `${data.basis.sample_size} similar jobs in the last ${data.basis.historical_window_days} days`
                : data.basis.data_source === "active_helpers"
                ? `${data.basis.sample_size} active Helpers in this category`
                : "London category benchmarks"}{" "}
              · {data.basis.location_tier === "inner_london" ? "Inner London" : data.basis.location_tier === "outer_london" ? "Outer London" : "Greater London"}
              {" · "}~{Math.round(data.duration_minutes)} min at {fmtGBP(data.hourly_rate.expected)}/hr
            </p>

            <p className="mt-2 flex items-start gap-1.5 rounded-md border border-border bg-muted/30 px-2.5 py-2 text-[11px] text-muted-foreground">
              <Info className="mt-0.5 h-3 w-3 shrink-0" aria-hidden />
              <span>{data.disclaimer}</span>
            </p>
          </>
        )}
      </div>
    </section>
  );
}

function Tier({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone: "muted" | "primary";
}) {
  return (
    <div
      className={`rounded-xl border px-3 py-2.5 text-center ${
        tone === "primary"
          ? "border-primary/30 bg-primary/5"
          : "border-border bg-background"
      }`}
    >
      <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </p>
      <p
        className={`mt-0.5 text-lg font-semibold tabular-nums ${
          tone === "primary" ? "text-primary" : "text-foreground"
        }`}
      >
        {value}
      </p>
    </div>
  );
}
